import type {
  LoginCredentials,
  LoginResponse,
  SignupCredentials,
  SignupResponse,
  Verify2FAResponse,
  MeResponse,
} from '@/types/auth';
import { apiRequest } from '@/lib/api';

const API_BASE = '/api/auth';

export async function fetchCurrentUser(): Promise<MeResponse> {
  return apiRequest(`${API_BASE}/me`);
}

export async function login(credentials: LoginCredentials): Promise<LoginResponse> {
  return apiRequest(`${API_BASE}/login`, {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
}

export async function verify2FA(code: string): Promise<Verify2FAResponse> {
  return apiRequest(`${API_BASE}/verify-2fa`, {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export async function signup(credentials: SignupCredentials): Promise<SignupResponse> {
  return apiRequest(`${API_BASE}/signup`, {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
}

export async function logout(): Promise<{ success: boolean }> {
  return apiRequest(`${API_BASE}/logout`, {
    method: 'POST',
  });
}

export async function resendConfirmation(): Promise<{ success: boolean; error?: string }> {
  return apiRequest(`${API_BASE}/resend-confirmation`, {
    method: 'POST',
  });
}

export async function confirmEmail(code: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
  already_confirmed?: boolean;
  redirect?: string;
}> {
  return apiRequest(`${API_BASE}/confirm/${code}`);
}

export async function resetPassword(token: string, newPassword: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
  redirect?: string;
}> {
  return apiRequest(`${API_BASE}/reset-password/${token}`, {
    method: 'POST',
    body: JSON.stringify({ password: newPassword }),
  });
}

export async function reauth(password: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  return apiRequest(`${API_BASE}/reauth`, {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

export async function getOAuthAccountSelection(sessionId: string): Promise<{
  success: boolean;
  provider?: string;
  email?: string;
  users?: Array<{
    id: string;
    email: string;
    full_name: string | null;
    created_at: string | null;
  }>;
  error?: string;
}> {
  return apiRequest(`${API_BASE}/oauth/account-select/${sessionId}`);
}

export async function selectOAuthAccount(sessionId: string, userId: string): Promise<{
  success: boolean;
  redirect?: string;
  error?: string;
}> {
  return apiRequest(`${API_BASE}/oauth/select-account/${sessionId}`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  });
}

export async function getOAuthLinkConfirmation(sessionId: string): Promise<{
  success: boolean;
  provider?: string;
  email?: string;
  existing_user?: {
    id: string;
    email: string;
    full_name: string | null;
    created_at: string | null;
  };
  error?: string;
}> {
  return apiRequest(`${API_BASE}/oauth/link-confirm/${sessionId}`);
}

export async function confirmOAuthLink(sessionId: string): Promise<{
  success: boolean;
  redirect?: string;
  error?: string;
}> {
  return apiRequest(`${API_BASE}/oauth/confirm-link/${sessionId}`, {
    method: 'POST',
  });
}

export async function getGithubRepos(): Promise<{
  success: boolean;
  repos?: Array<{
    id: number;
    name: string;
    full_name: string;
    private: boolean;
    html_url: string;
    description?: string;
  }>;
  error?: string;
}> {
  return apiRequest(`${API_BASE}/github/repos`);
}
